import "@app/App.css";
import { DataTable } from "@app/grid/DataTable";
import CompozzSpinner from "@comps/ui/CompozzSpinner";
import { TW_CompozzSpinnerContainer, TW_ObjectModelView_horizontal, TW_ObjectModelView_vertical } from "@utils/twClasses";
import { useCallback, useEffect, useRef, useState } from "react";
import { abortViewFetch, useDataStore, useModelStore } from "src/store";
import ObjectModelViewHeader from "./ObjectModelViewHeader";
import { useLoading } from "@utils/useLoading";
import { getFieldByPathKeys } from "@utils/model";
import { useTranslation } from "react-i18next";


interface Status {
  label: string;
  value: any;
}

function ObjectModelView() {

  const workspace = useModelStore(state => state.selectedWorkspace);
  const objectInfos = useModelStore(state => state.selectedObjectInfos);
  const view = useModelStore(state => state.selectedView);
  const data = useDataStore(state => state.data);
  const loadProgress = useDataStore(state => state.loadProgress);
  const fieldColumns = useDataStore(state => state.fieldColumns);
  const fetchView = useDataStore(state => state.fetchView);
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const focus = useDataStore(state => state.focus);
  const selectRows = useDataStore(state => state.setRowSelection);
  const fieldByKeys = useModelStore(state => state.fieldByKeys);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const fieldByPathKeys = getFieldByPathKeys(objectInfos, objectInfos?.fields || [], relationByKeys, fieldByKeys);
  const enableRowActions = useDataStore(state => state.enableRowActions);
  const [statuses, setStatuses] = useState<Status[] | null>(new Array<Status>());
  const { t } = useTranslation();
  
  // const loadData = useDataStore(state => state.loadData);
  // const stopLoadingData = useDataStore(state => state.stopLoadingData);
  const period = useDataStore(state => state.period);
  // const loadingData = useDataStore(state => state.loadingData);
  const {loading, startLoading, stopLoading} = useLoading();
  const [searchText, setSearchText] = useState("");
  // The view whose lines are on screen, and whether it is being refreshed rather
  // than opened. A refresh leaves the grid on screen, on the same columns to chain
  // the changes on them, and brings it back to its first line (the lines load
  // again from the first page).
  const displayedViewRef = useRef<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [scrollTopResetKey, setScrollTopResetKey] = useState(0);

  const handleSearchTextChange = useCallback((text: string) => {
    setSearchText(text);
    selectRows({});
  }, [selectRows]);

  // Reset search when view or object changes
  useEffect(() => {
    setSearchText("");
  }, [view?._key, objectInfos?.object._key]);

  useEffect(() => {
    // An aborted fetch resolves immediately: without this flag its stopLoading
    // would hide the spinner started by the fetch that superseded it.
    let superseded = false;
    // The displayed view has changed (column moved or hidden, filter, period...):
    // it keeps its key, unlike a view being opened
    const viewId = workspace?._key + "/" + objectInfos?.object._key + "/" + view?._key;
    const isRefresh = displayedViewRef.current === viewId;
    if (isRefresh) {
      setScrollTopResetKey(key => key + 1);
    } else {
      displayedViewRef.current = null;
    }
    setRefreshing(isRefresh);
    startLoading();
    selectRows({});
    setStatuses(new Array<Status>());
    focus(null);
    enableRowActions(true);
    // Progressive (paged) load when the view opens or changes; mutation refreshes
    // go through fetchData, which reloads in one request without collapsing the grid.
    fetchView(view).then(() => {
      if (!superseded) {
        displayedViewRef.current = viewId;
        stopLoading();
      }
    });
    return () => {
      superseded = true;
      abortViewFetch();
    };
  }, [workspace, objectInfos, view, period]);


  if (!workspace || !objectInfos) {
    return null;
  }

  const fields = fieldColumns;
  const style = {
    scrollbarColor: wsColor + " " + wsLightColor
  }

  const groups = view?.groups?.filter(group => fieldByPathKeys.get(group.pathKey)) || [];

  const statusesStyle = {
    width: "fit-content",
    backgroundColor: wsLightColor,
    zIndex: 50,
    border: "1px solid " + wsColor,
    fontSize: "12px",
    fontWeight: "bold",
    color: wsColor,
  }

  const statusSeperator = t('data.table.status.separator');
  return (
    <section key={"object-model-view-" + objectInfos?.object._key + "-" + view?._key} className={TW_ObjectModelView_vertical} style={style}>
      <ObjectModelViewHeader searchText={searchText} setSearchText={handleSearchTextChange} />
      {(!loading || refreshing) && <div className={TW_ObjectModelView_horizontal}>
        <DataTable fieldColumns={fields} lines={data} groups={groups} searchText={searchText} onStatusesChange={setStatuses} scrollTopResetKey={scrollTopResetKey}/>
      </div>}
      {loading && <div className={TW_CompozzSpinnerContainer + (refreshing ? " z-50" : "")}>
        <CompozzSpinner />
      </div>}
      {((statuses && statuses.length > 0) || loadProgress) &&
          <div key={"statuses-" + objectInfos?.object._key + "-" + view?._key + "-wrapper"} className="absolute bottom-4 right-4 w-fit bg-white z-50 flex flex-row justify-end items-end gap-2">
            {loadProgress &&
              <div key={"status-load-progress"} className="text-center text-gray-400 py-1 px-2 text-sm text-nowrap whitespace-nowrap rounded-md" style={statusesStyle}>{t('data.table.status.loading')}{statusSeperator}{loadProgress.loaded.toLocaleString()} / {loadProgress.total.toLocaleString()}</div>
            }
            {statuses && statuses.map((status) => (
              <div key={"status-" + status.label + "-" + status.value} className="text-center text-gray-400 py-1 px-2 text-sm text-nowrap whitespace-nowrap rounded-md" style={statusesStyle}>{status.label}{statusSeperator}{status.value}</div>
            ))}
          </div>
        }
    </section>
  );
};

export default ObjectModelView;